import React, { useMemo, useState } from 'react';
import { Card, Table, Typography, Empty, Skeleton, Dropdown, Button } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useResponsive } from '../../hooks/useResponsive';
import { useCurrency } from '../../hooks/useCurrency';
import { useGetEmployeePerformanceQuery, useGetCashierPerformanceQuery } from '../../services/reportsApi';
import { useAppSelector } from '../../hooks/redux';
import { exportCSV, exportExcel, exportPDF, getPeriodLabel, ExportConfig } from '../../utils/reportExport';
import { useRestaurantTimeZone } from '../../hooks/useTimeZone';
import dayjs from 'dayjs';

const { Text } = Typography;

interface EmployeePerformanceProps {
  selectedPeriod: string;
  customDateRange?: [dayjs.Dayjs, dayjs.Dayjs] | null;
}

const EmployeePerformance: React.FC<EmployeePerformanceProps> = ({
  selectedPeriod,
  customDateRange,
}) => {
  const { isMobile } = useResponsive();
  const { currencySymbol } = useCurrency();
  const { currentRestaurant } = useAppSelector((state) => state.restaurant);
  const { startOfDay, startOfWeek, startOfMonth, formatInTimeZone } = useRestaurantTimeZone();

  // Calculate date range based on selected period using restaurant timezone
  const dateRange = useMemo(() => {
    const today = startOfDay();

    if (customDateRange && customDateRange[0] && customDateRange[1]) {
      return {
        fromDate: customDateRange[0].format('YYYY-MM-DD'),
        toDate: customDateRange[1].format('YYYY-MM-DD'),
      };
    }

    switch (selectedPeriod) {
      case 'today':
        return {
          fromDate: today.format('YYYY-MM-DD'),
          toDate: today.format('YYYY-MM-DD'),
        };
      case 'week':
        return {
          fromDate: startOfWeek().format('YYYY-MM-DD'),
          toDate: today.format('YYYY-MM-DD'),
        };
      case 'month':
        return {
          fromDate: startOfMonth().format('YYYY-MM-DD'),
          toDate: today.format('YYYY-MM-DD'),
        };
      case 'custom':
        if (!customDateRange) {
          return { fromDate: '', toDate: '' };
        }
        return {
          fromDate: customDateRange[0].format('YYYY-MM-DD'),
          toDate: customDateRange[1].format('YYYY-MM-DD'),
        };
      default:
        return {
          fromDate: today.format('YYYY-MM-DD'),
          toDate: today.format('YYYY-MM-DD'),
        };
    }
  }, [selectedPeriod, customDateRange, startOfDay, startOfWeek, startOfMonth]);

  const { data: employees, isLoading: loadingEmployees, error: errorEmployees } = useGetEmployeePerformanceQuery(
    {
      restaurantId: currentRestaurant?.id || '',
      fromDate: dateRange.fromDate,
      toDate: dateRange.toDate,
    },
    { skip: !currentRestaurant?.id || !dateRange.fromDate || !dateRange.toDate }
  );

  const { data: cashiers, isLoading: loadingCashiers, error: errorCashiers } = useGetCashierPerformanceQuery(
    {
      restaurantId: currentRestaurant?.id || '',
      fromDate: dateRange.fromDate,
      toDate: dateRange.toDate,
    },
    { skip: !currentRestaurant?.id || !dateRange.fromDate || !dateRange.toDate }
  );

  // Calculate employee statistics
  const employeeStats = useMemo(() => {
    if (!employees || employees.length === 0) {
      return {
        totalEmployees: 0,
        totalSales: 0,
        totalOrders: 0,
        totalTips: 0,
        avgSalesPerEmployee: 0,
      };
    }

    const totalSales = employees.reduce((sum, e) => sum + e.totalSales, 0);
    const totalOrders = employees.reduce((sum, e) => sum + e.ordersCount, 0);
    const totalTips = employees.reduce((sum, e) => sum + e.tips, 0);

    return {
      totalEmployees: employees.length,
      totalSales,
      totalOrders,
      totalTips,
      avgSalesPerEmployee: totalSales / employees.length,
    };
  }, [employees]);

  // Calculate cashier statistics
  const cashierStats = useMemo(() => {
    if (!cashiers || cashiers.length === 0) {
      return {
        totalCashiers: 0,
        totalShifts: 0,
        totalSales: 0,
        avgVariance: 0,
      };
    }

    const totalShifts = cashiers.reduce((sum, c) => sum + c.shiftsCount, 0);
    const totalSales = cashiers.reduce((sum, c) => sum + c.totalSales, 0);
    const avgVariance = cashiers.reduce((sum, c) => sum + c.averageVariance, 0) / cashiers.length;

    return {
      totalCashiers: cashiers.length,
      totalShifts,
      totalSales,
      avgVariance,
    };
  }, [cashiers]);

  // Values wear ink with tabular figures; color is reserved for signals
  // (variance). Sales get a proportion bar so the ranking reads at a glance.
  const moneyCell = (value: number, strong = false) => (
    <Text strong={strong} style={{ fontVariantNumeric: 'tabular-nums' }}>
      {currencySymbol} {value.toFixed(2)}
    </Text>
  );

  const maxEmployeeSales = Math.max(...(employees || []).map(e => e.totalSales), 1);
  const maxCashierSales = Math.max(...(cashiers || []).map(c => c.totalSales), 1);

  const salesBarCell = (sales: number, max: number) => (
    <div style={{ minWidth: 120 }}>
      <Text strong style={{ fontVariantNumeric: 'tabular-nums' }}>
        {currencySymbol} {sales.toFixed(2)}
      </Text>
      <div style={{ height: 4, borderRadius: 2, background: 'var(--bg-tertiary)', marginTop: 3 }}>
        <div style={{
          height: '100%',
          width: `${(sales / max) * 100}%`,
          borderRadius: 2,
          background: 'var(--color-primary)',
        }} />
      </div>
    </div>
  );

  const employeeColumns = [
    {
      title: '#',
      dataIndex: 'rank',
      key: 'rank',
      width: 44,
      render: (rank: number) => <Text type="secondary">{rank}</Text>,
    },
    {
      title: 'Mesero',
      dataIndex: 'userName',
      key: 'userName',
      render: (name: string) => (
        <Text strong style={{ fontSize: isMobile ? '13px' : '14px' }}>{name}</Text>
      ),
    },
    {
      title: 'Órdenes',
      dataIndex: 'ordersCount',
      key: 'ordersCount',
      width: isMobile ? 70 : 90,
      align: 'center' as const,
      sorter: (a: any, b: any) => a.ordersCount - b.ordersCount,
      render: (count: number) => <Text style={{ fontVariantNumeric: 'tabular-nums' }}>{count}</Text>,
    },
    {
      title: 'Ventas',
      dataIndex: 'totalSales',
      key: 'totalSales',
      width: isMobile ? 120 : 160,
      align: 'right' as const,
      sorter: (a: any, b: any) => a.totalSales - b.totalSales,
      defaultSortOrder: 'descend' as const,
      render: (sales: number) => salesBarCell(sales, maxEmployeeSales),
    },
    {
      title: 'Ticket Prom.',
      dataIndex: 'averageTicket',
      key: 'averageTicket',
      width: isMobile ? 90 : 110,
      align: 'right' as const,
      render: (avg: number) => moneyCell(avg),
    },
    {
      title: 'Propinas',
      dataIndex: 'tips',
      key: 'tips',
      width: isMobile ? 80 : 100,
      align: 'right' as const,
      render: (tips: number) => moneyCell(tips),
    },
    {
      title: '% Total',
      dataIndex: 'salesPercentage',
      key: 'salesPercentage',
      width: isMobile ? 70 : 90,
      align: 'center' as const,
      render: (percentage: number) => (
        <Text type="secondary" style={{ fontVariantNumeric: 'tabular-nums' }}>{percentage.toFixed(1)}%</Text>
      ),
    },
  ];

  const cashierColumns = [
    {
      title: '#',
      dataIndex: 'rank',
      key: 'rank',
      width: 44,
      render: (rank: number) => <Text type="secondary">{rank}</Text>,
    },
    {
      title: 'Cajero',
      dataIndex: 'userName',
      key: 'userName',
      render: (name: string) => (
        <Text strong style={{ fontSize: isMobile ? '13px' : '14px' }}>{name}</Text>
      ),
    },
    {
      title: 'Turnos',
      dataIndex: 'shiftsCount',
      key: 'shiftsCount',
      width: isMobile ? 60 : 80,
      align: 'center' as const,
      sorter: (a: any, b: any) => a.shiftsCount - b.shiftsCount,
      render: (count: number) => <Text style={{ fontVariantNumeric: 'tabular-nums' }}>{count}</Text>,
    },
    {
      title: 'Efectivo',
      dataIndex: 'cashSales',
      key: 'cashSales',
      width: isMobile ? 90 : 110,
      align: 'right' as const,
      render: (sales: number) => moneyCell(sales),
    },
    {
      title: 'Tarjeta',
      dataIndex: 'cardSales',
      key: 'cardSales',
      width: isMobile ? 90 : 110,
      align: 'right' as const,
      render: (sales: number) => moneyCell(sales),
    },
    {
      title: 'Transfer.',
      dataIndex: 'transferSales',
      key: 'transferSales',
      width: isMobile ? 90 : 110,
      align: 'right' as const,
      render: (sales: number) => moneyCell(sales),
    },
    {
      title: 'Total',
      dataIndex: 'totalSales',
      key: 'totalSales',
      width: isMobile ? 120 : 160,
      align: 'right' as const,
      sorter: (a: any, b: any) => a.totalSales - b.totalSales,
      defaultSortOrder: 'descend' as const,
      render: (sales: number) => salesBarCell(sales, maxCashierSales),
    },
    {
      // Variance is a SIGNAL: red = missing cash, amber = surplus, neutral = exact.
      title: 'Diferencia Prom.',
      dataIndex: 'averageVariance',
      key: 'averageVariance',
      width: isMobile ? 100 : 130,
      align: 'right' as const,
      render: (variance: number) => (
        <Text
          strong={variance !== 0}
          style={{
            fontVariantNumeric: 'tabular-nums',
            color: variance < 0
              ? 'var(--color-error-text)'
              : variance > 0
                ? 'var(--color-warning-text)'
                : 'var(--color-neutral-text)',
          }}
        >
          {variance === 0 ? '✓ ' : ''}{currencySymbol} {variance.toFixed(2)}
        </Text>
      ),
    },
  ];

  const handleExport = (format: 'csv' | 'excel' | 'pdf') => {
    const periodLabel = getPeriodLabel(selectedPeriod, customDateRange);
    const config: ExportConfig = {
      reportTitle: 'Reporte de Empleados',
      periodLabel,
      restaurantName: currentRestaurant?.name || 'N/A',
      timestamp: formatInTimeZone(new Date(), 'DD/MM/YYYY HH:mm'),
      sections: [],
    };

    if (employees && employees.length > 0) {
      config.sections.push({
        title: 'Rendimiento de Meseros',
        titleStyle: 'employee',
        headers: ['Mesero', 'Órdenes', 'Ventas', 'Ticket Prom.', 'Propinas', '% Total'],
        rows: employees.map(e => [
          e.userName, e.ordersCount, `${currencySymbol} ${e.totalSales.toFixed(2)}`,
          `${currencySymbol} ${e.averageTicket.toFixed(2)}`, `${currencySymbol} ${e.tips.toFixed(2)}`,
          `${e.salesPercentage.toFixed(1)}%`,
        ]),
      });
    }

    if (cashiers && cashiers.length > 0) {
      config.sections.push({
        title: 'Rendimiento de Cajeros',
        titleStyle: 'cashier',
        headers: ['Cajero', 'Turnos', 'Efectivo', 'Tarjeta', 'Transfer.', 'Total', 'Varianza'],
        rows: cashiers.map(c => [
          c.userName, c.shiftsCount, `${currencySymbol} ${c.cashSales.toFixed(2)}`,
          `${currencySymbol} ${c.cardSales.toFixed(2)}`, `${currencySymbol} ${c.transferSales.toFixed(2)}`,
          `${currencySymbol} ${c.totalSales.toFixed(2)}`, `${currencySymbol} ${c.averageVariance.toFixed(2)}`,
        ]),
      });
    }

    if (format === 'csv') exportCSV(config, 'empleados');
    else if (format === 'excel') exportExcel(config, 'empleados');
    else exportPDF(config);
  };

  const [activeView, setActiveView] = useState<'employees' | 'cashiers'>('employees');

  const isLoading = loadingEmployees || loadingCashiers;
  const hasError = errorEmployees || errorCashiers;

  if (hasError) {
    return (
      <Card>
        <Empty
          description="Error al cargar los datos de empleados"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card>
        <Skeleton active paragraph={{ rows: 8 }} />
      </Card>
    );
  }

  const kpi = (label: string, value: React.ReactNode, sub?: string, color?: string) => (
    <div className="rep-kpi">
      <div className="rep-kpi__label">{label}</div>
      <div className="rep-kpi__value" style={color ? { color } : undefined}>{value}</div>
      {sub && <div className="rep-kpi__sub">{sub}</div>}
    </div>
  );

  const money = (n: number) =>
    `${currencySymbol}${(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div>
      {/* Views as pills (no nested tab bar) + export on the same row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        <div className="rep-subpills" style={{ marginBottom: 0 }}>
          <button
            type="button"
            className={`rep-subpill${activeView === 'employees' ? ' rep-subpill--selected' : ''}`}
            onClick={() => setActiveView('employees')}
          >
            Meseros · {employees?.length || 0}
          </button>
          <button
            type="button"
            className={`rep-subpill${activeView === 'cashiers' ? ' rep-subpill--selected' : ''}`}
            onClick={() => setActiveView('cashiers')}
          >
            Cajeros · {cashiers?.length || 0}
          </button>
        </div>
        <Dropdown menu={{ items: [
          { key: 'csv', label: 'CSV', onClick: () => handleExport('csv') },
          { key: 'excel', label: 'Excel', onClick: () => handleExport('excel') },
          { key: 'pdf', label: 'PDF', onClick: () => handleExport('pdf') },
        ]}}>
          <Button icon={<DownloadOutlined />}>Exportar</Button>
        </Dropdown>
      </div>

      {activeView === 'employees' ? (
        <>
          <div className="rep-kpis">
            {kpi('Meseros', employeeStats.totalEmployees)}
            {kpi('Ventas', money(employeeStats.totalSales))}
            {kpi('Órdenes', employeeStats.totalOrders)}
            {kpi('Propinas', money(employeeStats.totalTips))}
          </div>

          {!employees || employees.length === 0 ? (
            <Card>
              <Empty description="No hay datos de meseros para este periodo" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            </Card>
          ) : (
            <div className="rep-panel">
              <Table
                columns={employeeColumns}
                dataSource={employees.map((item, index) => ({
                  ...item,
                  key: item.userId,
                  rank: index + 1,
                }))}
                pagination={{
                  pageSize: isMobile ? 10 : 15,
                  showSizeChanger: !isMobile,
                  showTotal: (total) => `Total: ${total} meseros`,
                }}
                size="small"
                scroll={{ x: isMobile ? 600 : undefined }}
              />
            </div>
          )}
        </>
      ) : (
        <>
          <div className="rep-kpis">
            {kpi('Cajeros', cashierStats.totalCashiers)}
            {kpi('Turnos', cashierStats.totalShifts)}
            {kpi('Ventas', money(cashierStats.totalSales))}
            {kpi(
              'Diferencia promedio',
              `${cashierStats.avgVariance === 0 ? '✓ ' : ''}${money(cashierStats.avgVariance)}`,
              cashierStats.avgVariance === 0 ? 'cajas cuadradas' : undefined,
              cashierStats.avgVariance < 0
                ? 'var(--color-error-text)'
                : cashierStats.avgVariance > 0
                  ? 'var(--color-warning-text)'
                  : undefined,
            )}
          </div>

          {!cashiers || cashiers.length === 0 ? (
            <Card>
              <Empty description="No hay datos de cajeros para este periodo" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            </Card>
          ) : (
            <div className="rep-panel">
              <Table
                columns={cashierColumns}
                dataSource={cashiers.map((item, index) => ({
                  ...item,
                  key: item.userId,
                  rank: index + 1,
                }))}
                pagination={{
                  pageSize: isMobile ? 10 : 15,
                  showSizeChanger: !isMobile,
                  showTotal: (total) => `Total: ${total} cajeros`,
                }}
                size="small"
                scroll={{ x: isMobile ? 700 : undefined }}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EmployeePerformance;
